import { axios } from '@/lib/axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

export const getALs = async (query) => {
  const { page, pageSize, sortBy = '', orderBy = 1, filters = {} } = query

  // Transform array filters to comma-separated strings for backend
  const transformedFilters = { ...filters }
  Object.keys(transformedFilters).forEach((key) => {
    if (Array.isArray(transformedFilters[key])) {
      transformedFilters[key] = transformedFilters[key].join(',')
    }
  })

  const params = {
    page,
    limit: pageSize,
    sortBy: sortBy || 'country', // Default to 'country' to avoid empty string error
    orderBy,
    ...transformedFilters,
  }
  const response = await axios.get('/ALs', { params })
  return response.data
}

export const createAL = async (data) => {
  const response = await axios.post('/ALs', data)
  return response.data
}

export const updateAL = async ({ id, data }) => {
  const response = await axios.put(`/ALs/${id}`, data)
  return response.data
}

export const deleteAL = async (id) => {
  const response = await axios.delete(`/ALs/${id}`)
  return response.data
}

export const uploadALs = async (file) => {
  const formData = new FormData()
  formData.append('file', file)
  const response = await axios.post('/ALs/upload', formData)
  return response.data
}

const useAL = () => {
  const queryClient = useQueryClient()

  const useFetchAllALs = (query) => {
    return useQuery({
      queryKey: ['als', query],
      queryFn: () => getALs(query),
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to fetch ALs')
      },
    })
  }

  const useCreateAL = () => {
    return useMutation({
      mutationFn: createAL,
      onSuccess: () => {
        queryClient.invalidateQueries(['als'])
        toast.success('AL created successfully')
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to create AL')
      },
    })
  }

  const useUpdateAL = () => {
    return useMutation({
      mutationFn: updateAL,
      onSuccess: () => {
        queryClient.invalidateQueries(['als'])
        toast.success('AL updated successfully')
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to update AL')
      },
    })
  }

  const useRemoveAL = () => {
    return useMutation({
      mutationFn: deleteAL,
      onSuccess: () => {
        queryClient.invalidateQueries(['als'])
        toast.success('AL deleted successfully')
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to delete AL')
      },
    })
  }

  const useUploadALs = () => {
    return useMutation({
      mutationFn: uploadALs,
      onSuccess: () => {
        queryClient.invalidateQueries(['als'])
        toast.success('ALs uploaded successfully')
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to upload ALs')
      },
    })
  }

  return {
    useFetchAllALs,
    useCreateAL,
    useUpdateAL,
    useRemoveAL,
    useUploadALs,
  }
}

export default useAL
